/**
 * Admin Finance API Service
 * Handles financial reports and summaries
 */

import { get } from '../client';

// === Response Types ===

export interface FinanceSummary {
  total_bookings: number;
  total_visitors: number;
  total_gross: number;
  total_discount: number;
  total_revenue: number;
  total_service_fee: number;
  total_commission: number;
  total_net_income: number;
}

export interface TicketRevenue {
  ticket_name: string;
  total_sold: number;
  revenue: number;
}

export interface PartnerRevenue {
  partner_name: string;
  total_bookings: number;
  total_commission: number;
}

interface FinanceSummaryResponse {
  success: boolean;
  data: {
    period: { startDate: string; endDate: string };
    summary: FinanceSummary;
    byTicket: TicketRevenue[];
    byPartner: PartnerRevenue[];
  };
}

export interface DailyRevenue {
  date: string;
  total_bookings: number;
  total_visitors: number;
  revenue: number;
  net_income: number;
}

interface DailyReportResponse {
  success: boolean;
  data: {
    month: number;
    year: number;
    daily: DailyRevenue[];
  };
}

export interface PartnerCommission {
  partner_id: string;
  partner_name: string;
  partner_email: string;
  bank_name: string | null;
  bank_account_number: string | null;
  bank_account_name: string | null;
  total_bookings: number;
  total_tickets_sold: number;
  total_commission: number;
}

interface CommissionsResponse {
  success: boolean;
  data: {
    period: { startDate: string; endDate: string };
    commissions: PartnerCommission[];
  };
}

// === API Functions ===

/**
 * Get finance summary for a date range
 */
export async function getFinanceSummary(
  startDate?: string,
  endDate?: string
): Promise<FinanceSummaryResponse['data']> {
  const response = await get<FinanceSummaryResponse>('/admin/finance/summary', {
    start_date: startDate,
    end_date: endDate
  });
  return response.data;
}

/**
 * Get daily revenue report for a month
 */
export async function getDailyReport(
  month?: number,
  year?: number
): Promise<DailyReportResponse['data']> {
  const response = await get<DailyReportResponse>('/admin/finance/daily', {
    month: month,
    year: year
  });
  return response.data;
}

/**
 * Get partner commissions for a date range
 */
export async function getPartnerCommissions(
  startDate?: string,
  endDate?: string
): Promise<CommissionsResponse['data']> {
  const response = await get<CommissionsResponse>('/admin/finance/commissions', {
    start_date: startDate,
    end_date: endDate
  });
  return response.data;
}
